import React, { FC, useEffect, useRef } from 'react';
import fp from 'lodash/fp';
import { Box } from '@mui/system';

import { Stack } from '../../../Stack';
import { Typography } from '../../../Typography';
import { Button2 } from '../../../Button2';


interface ListProps {
    type: 'hours' | 'minutes';
    valueHandler: React.Dispatch<React.SetStateAction<Date>>;
    selectedDate?: Date;
}

type GetSelectedValue = (type: ListProps['type'], selectedDate?: Date) => number;


const HOURS_RANGE = fp.range(0, 24);
const MINUTES_RANGE = fp.range(0, 60);


const getSelectedValue: GetSelectedValue = (type, selectedDate) => {
    if (!selectedDate)
        return undefined;

    return type === 'hours' ? selectedDate.getHours() : selectedDate.getMinutes();
};


export const List: FC<ListProps> = React.memo(({
    type,
    selectedDate,
    valueHandler,
}) => {
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef)
            scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [scrollRef, selectedDate]);

    const selected = getSelectedValue(type, selectedDate);

    const selectorHandler = value => valueHandler(() => {
        const newDate = selectedDate ? new Date(selectedDate) : new Date();

        if (type === 'hours')
            newDate.setHours(value);
        else
            newDate.setMinutes(value);
        return newDate;
    });

    return (
        <Stack
            width={({ spacing }) => spacing(8)}
            position='relative'
        >
            <Typography
                variant='regularCaption3'
                color='grayscale.shade85'
                py={2}
                px={0.5}
                mb={1}
                textAlign='center'
            >
                {type === 'hours' ? 'Час' : 'Мин.'}
            </Typography>

            <Stack
                spacing={1}
                alignItems='center'
                sx={{
                    overflow: 'auto',
                    maxHeight: ({ spacing }) => spacing(43),

                    '::-webkit-scrollbar': {
                        display: 'none',
                    },

                    '&:before': {
                        content: '""',
                        position: 'absolute',
                        zIndex: 1,
                        width: '100%',
                        height: '10%',
                        top: 35,
                        background: 'linear-gradient(360deg, rgba(139,167,32,0) 0%, rgba(255,255,255,1) 100%)',
                        pointerEvents: 'none',
                    },

                    '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '100%',
                        height: '15%',
                        bottom: 0,
                        background: 'linear-gradient(180deg, rgba(139,167,32,0) 0%, rgba(255,255,255,1) 100%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                {(type === 'hours' ? HOURS_RANGE : MINUTES_RANGE).map(value => (
                    <Box
                        key={value}
                        {...selected === value && {
                            ref: ref => {
                                scrollRef.current = ref;
                            },
                        }}
                        width='100%'
                        sx={{ scrollMargin: '25px' }}
                    >
                        <Button2
                            onClick={() => selectorHandler(value)}

                            variant='secondary'
                            fullWidth

                            sx={{
                                px: 1,
                                py: 1.5,
                                minWidth: 0,
                                borderRadius: 2,

                                ...selected === value && {
                                    backgroundColor: 'general.primary50',
                                    color: 'grayscale.shade0',

                                    '&:not(:focus), &:hover, &:not(:active)': {
                                        backgroundColor: 'general.primary50',
                                    },
                                },
                            }}
                        >
                            <Typography
                                textAlign='center'
                                variant='regularCaption1'
                            >
                                {`${value}`.padStart(2, '0')}
                            </Typography>
                        </Button2>
                    </Box>
                ))}
            </Stack>
        </Stack>
    );
});
